// src/store/allRequestsSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from './index';
import {
  getAllRequests,
  AssetRequest,
  cancelRequest,
  createAssetRequest,
  getMyRequests,
  getRequest,
  CreateRequestData,
  putOnHold,
  resumeRequest,
  PutOnHoldPayload
} from '../api/assetsAPI';

interface AllRequestsState {
  requests: AssetRequest[];
  myRequests: AssetRequest[];
  loading: boolean;
  error: string | null;
  currentRequest: AssetRequest | null;
}

const initialState: AllRequestsState = {
  requests: [],
  myRequests: [],
  loading: false,
  error: null,
  currentRequest: null
};

// -------- Fetch all requests --------
export const fetchAllRequests = createAsyncThunk<
  AssetRequest[],
  void,
  { rejectValue: string }
>('allRequests/fetchAllRequests', async (_, { rejectWithValue }) => {
  try {
    const requests = await getAllRequests();
    return requests;
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch all requests'
    );
  }
});

// -------- Create request --------
export const createRequest = createAsyncThunk<
  AssetRequest,
  CreateRequestData,
  { rejectValue: string }
>('allRequests/createRequest', async (requestData, { rejectWithValue }) => {
  try {
    const request = await createAssetRequest(requestData);
    return request;
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create request'
    );
  }
});

// -------- Get my requests --------
export const fetchMyRequests = createAsyncThunk<
  AssetRequest[],
  void,
  { rejectValue: string }
>('allRequests/fetchMyRequests', async (_, { rejectWithValue }) => {
  try {
    const requests = await getMyRequests();
    return requests;
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch my requests'
    );
  }
});

// -------- Get single request --------
export const fetchRequest = createAsyncThunk<
  AssetRequest,
  number,
  { rejectValue: string }
>('allRequests/fetchRequest', async (requestId, { rejectWithValue }) => {
  try {
    const request = await getRequest(requestId);
    return request;
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch request'
    );
  }
});

// -------- Cancel a request --------
export const cancelRequestThunk = createAsyncThunk<
  number,
  { requestId: number; cancellationReason?: string },
  { rejectValue: string }
>(
  'allRequests/cancelRequest',
  async ({ requestId, cancellationReason }, { rejectWithValue }) => {
    console.log('🔄 cancelRequestThunk called with ID:', requestId);
    try {
      console.log('📡 Calling cancelRequest API...');
      await cancelRequest(requestId, cancellationReason);
      console.log(
        '✅ cancelRequest API successful, returning requestId:',
        requestId
      );
      return requestId;
    } catch (error: unknown) {
      console.error('❌ cancelRequestThunk error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to cancel request';
      return rejectWithValue(errorMessage);
    }
  }
);

// -------- Put request on hold --------
export const putOnHoldThunk = createAsyncThunk<
  AssetRequest,
  { requestId: number; payload: PutOnHoldPayload },
  { rejectValue: string }
>(
  'allRequests/putOnHold',
  async ({ requestId, payload }, { rejectWithValue, dispatch }) => {
    try {
      await putOnHold(requestId, payload);
      // Fetch the updated request to get the new status
      const updatedRequest = await getRequest(requestId);
      return updatedRequest;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to put request on hold';
      return rejectWithValue(errorMessage);
    }
  }
);

// -------- Resume request --------
export const resumeRequestThunk = createAsyncThunk<
  AssetRequest,
  number,
  { rejectValue: string }
>('allRequests/resumeRequest', async (requestId, { rejectWithValue }) => {
  try {
    await resumeRequest(requestId);
    // Fetch the updated request to get the new status
    const updatedRequest = await getRequest(requestId);
    return updatedRequest;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to resume request';
    return rejectWithValue(errorMessage);
  }
});

// -------- Slice --------
const allRequestsSlice = createSlice({
  name: 'allRequests',
  initialState,
  reducers: {
    clearCurrentRequest: state => {
      state.currentRequest = null;
    },
    clearError: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch all requests
      .addCase(fetchAllRequests.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchAllRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Create request
      .addCase(createRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.requests.unshift(action.payload);
        state.myRequests.unshift(action.payload);
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Fetch my requests
      .addCase(fetchMyRequests.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.myRequests = action.payload;
      })
      .addCase(fetchMyRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Fetch single request
      .addCase(fetchRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;

        // Update in requests array if exists
        const requestIndex = state.requests.findIndex(
          req => req.id === action.payload.id
        );
        if (requestIndex !== -1) {
          state.requests[requestIndex] = action.payload;
        }

        // Update in myRequests array if exists
        const myRequestIndex = state.myRequests.findIndex(
          req => req.id === action.payload.id
        );
        if (myRequestIndex !== -1) {
          state.myRequests[myRequestIndex] = action.payload;
        }
      })
      .addCase(fetchRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Cancel request
      .addCase(cancelRequestThunk.fulfilled, (state, action) => {
        console.log(
          '🔄 Redux: Processing cancel fulfillment for ID:',
          action.payload
        );
        console.log('📊 Before - requests count:', state.requests.length);
        console.log('📊 Before - myRequests count:', state.myRequests.length);

        state.requests = state.requests.filter(r => r.id !== action.payload);
        state.myRequests = state.myRequests.filter(
          r => r.id !== action.payload
        );

        console.log('📊 After - requests count:', state.requests.length);
        console.log('📊 After - myRequests count:', state.myRequests.length);

        if (state.currentRequest?.id === action.payload) {
          state.currentRequest = null;
        }
      })
      .addCase(cancelRequestThunk.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Put on hold
      .addCase(putOnHoldThunk.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(putOnHoldThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update request in both arrays
        const requestIndex = state.requests.findIndex(
          r => r.id === action.payload.id
        );
        if (requestIndex !== -1) {
          state.requests[requestIndex] = action.payload;
        }

        const myRequestIndex = state.myRequests.findIndex(
          r => r.id === action.payload.id
        );
        if (myRequestIndex !== -1) {
          state.myRequests[myRequestIndex] = action.payload;
        }

        if (state.currentRequest?.id === action.payload.id) {
          state.currentRequest = action.payload;
        }
      })
      .addCase(putOnHoldThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })

      // Resume request
      .addCase(resumeRequestThunk.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resumeRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update request in both arrays
        const requestIndex = state.requests.findIndex(
          r => r.id === action.payload.id
        );
        if (requestIndex !== -1) {
          state.requests[requestIndex] = action.payload;
        }

        const myRequestIndex = state.myRequests.findIndex(
          r => r.id === action.payload.id
        );
        if (myRequestIndex !== -1) {
          state.myRequests[myRequestIndex] = action.payload;
        }

        if (state.currentRequest?.id === action.payload.id) {
          state.currentRequest = action.payload;
        }
      })
      .addCase(resumeRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      });
  }
});

// -------- Selectors --------
export const selectAllRequests = (state: RootState) =>
  state.allRequests.requests;
export const selectMyRequests = (state: RootState) =>
  state.allRequests.myRequests;
export const selectAllRequestsLoading = (state: RootState) =>
  state.allRequests.loading;
export const selectAllRequestsError = (state: RootState) =>
  state.allRequests.error;
export const selectCurrentRequest = (state: RootState) =>
  state.allRequests.currentRequest;
export const selectRequestById = (state: RootState, requestId: number) =>
  state.allRequests.requests.find(request => request.id === requestId);

// -------- Actions --------
export const { clearCurrentRequest, clearError } = allRequestsSlice.actions;

export default allRequestsSlice.reducer;
